import React, { useEffect, useState } from "react";
import { $t } from "~/i18n.js";

import {
  CheckCircleFilled,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  AutoComplete,
  Button,
  Collapse,
  Descriptions,
  Flex,
  Input
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AuthorisationStatus } from "~/components/authorise/AuthorisationStatus.jsx";
import PincodeInput from "~/components/form/PincodeInput.jsx";
import { SelectUser } from "~/components/profile/selectUser.jsx";
import { api } from "~/lib/api.js";
import { _parseDate } from "~/lib/localize.js";
import { colors } from "~/theme/colors.js";

export const SignFlightCollapse = ({
  flight = {},
  updateFlight = (e) => {},
}) => {
  const { club } = useSelector((state) => state.persist);

  return (
    <Collapse
      style={{ marginTop: 12 }}
      items={[
        {
          key: "1",
          label: (
            <Flex justify="space-between">
              <p>{$t("Teken vlucht af")}</p>

              <div className="row" style={{ gap: 6 }}>
                {club.enable_authorization && (
                  <AuthorisationStatus
                    authorisation={flight.authorised}
                    pilotName={flight.gezagvoerder_naam}
                  />
                )}

                {flight.signed ? (
                  <CheckCircleFilled style={{ color: colors.groen_dark }} />
                ) : flight.authorised ? (
                  <CheckCircleFilled style={{ color: colors.red }} />
                ) : (
                  <CheckCircleOutlined style={{ color: colors.gray_light }} />
                )}
              </div>
            </Flex>
          ),
          children: (
            <SignFlight
              enable_authorization={club.enable_authorization}
              flight={flight}
              updateFlight={updateFlight}
            />
          ),
        },
      ]}
      defaultActiveKey={[]}
    />
  );
};

export const SignFlight = ({
  enable_authorization = false,
  flight = {},
  updateFlight = (e) => {},
}) => {
  const [revoking, setRevoking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remark, setRemark] = useState("");
  const [pincode, setPincode] = useState("");

  const [instructor, setInstructor] = useState(null);

  const dispatch = useDispatch();

  const readOnly = flight.club_tag;

  const setKey = (user) => {
    setInstructor(user);
  };

  // Current user to sign
  const { profile } = useSelector((state) => state.persist);
  useEffect(() => {
    if (profile.is_instructeur) {
      setInstructor(profile);
    }
  }, [profile]);

  const { vliegers } = useSelector((state) => state.flights);
  const possibleInstructors = vliegers.filter((v) => v.is_instructeur);

  const revokeSign = async () => {
    setRevoking(true);
    const { data } = await api.delete("flights/revoke_sign.json", {
      uuid: flight.uuid,
    });
    setRevoking(false);
    if (data) {
      dispatch({
        type: "UPDATE_FLIGHT",
        flightData: data,
      });
      updateFlight({ flight: data });
    }
  };

  const sign = async () => {
    if (!instructor) return;
    setLoading(true);
    const { data } = await api.post2("flights/sign.json", {
      uuid: flight.uuid,
      id: instructor.id,
      remark,
      pincode,
      save_as_note: true,
    });
    setLoading(false);
    setPincode("");
    if (data) {
      dispatch({
        type: "UPDATE_FLIGHT",
        flightData: data,
      });
      updateFlight({ flight: data });
    }
  };

  return (
    <section>
      {enable_authorization && (
        <div className="row" style={{ marginBottom: 12, gap: 6 }}>
          <span className="bold label">{$t("Authorisatie")}</span>
          <AuthorisationStatus
            authorisation={flight.authorised}
            pilotName={flight.gezagvoerder_naam}
          >
            {flight.authorised ? (
              <p>{flight.authorised?.name} <span className="small gray">{flight.authorised?.limitations}</span></p>
            ) : (
              <span>{$t("geen")}</span>
            )}
          </AuthorisationStatus>
        </div>
      )}

      {flight.signed && (
        <>
          <Descriptions size="small" column={1} layout="horizontal">
            <Descriptions.Item label={$t("Datum")}>
              {_parseDate(flight.signed?.date)}
            </Descriptions.Item>
            {flight.signed?.remark && (
              <Descriptions.Item label={$t("Opmerking")}>
                {flight.signed?.remark}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={$t("instructeur")}>
              {flight.signed?.name}{" "}
              {flight.signed?.brevet && `(${flight.signed?.brevet})`}
            </Descriptions.Item>
          </Descriptions>

          {flight.signed?.id == profile.id && (
            <Button
              size="small"
              loading={revoking}
              danger
              onClick={revokeSign}
              icon={<StopOutlined />}
            >
              {$t("Verwijder")}
            </Button>
          )}
        </>
      )}

      {readOnly && (
        <p>
          {$t(
            "Vlucht kan alleen afgetekend worden bij de club waar de club is gemaakt",
          )}
          .
        </p>
      )}

      {!flight.signed && !readOnly && (
        <section>
          <Flex gap={6} vertical>
            <p className="small">
              <span className="label">{$t("Piloot")}</span>
              <span style={{ marginLeft: 6 }}>
                {flight.is_fis
                  ? flight.tweede_inzittende_naam
                  : flight.gezagvoerder_naam}
              </span>
            </p>

            <p className="gray small">
              {$t(
                "Als instructeur kan je een vlucht met je pincode aftekenen, bijvoorbeeld trainingsvluchten of solovluchten",
              )}
              .{" "}
              {$t(
                "Opmerkingen worden tevens zichtbaar in het profiel van de gebruiker",
              )}
              .
            </p>

            <p>{$t("Teken vlucht af als")}</p>

            <SelectUser
              users={possibleInstructors}
              value={instructor}
              onChange={setKey}
            />

            <p>{$t("Opmerking")}</p>
            <Input.TextArea
              disabled={!instructor}
              value={remark}
              placeholder={profile.is_instructeur && $t("Eventuele opmerking")}
              onChange={(e) => setRemark(e.target.value)}
            />
            <p>{$t("Pincode")}:</p>
            <PincodeInput
              disabled={!instructor}
              value={pincode}
              onComplete={setPincode}
            />
          </Flex>

          <Flex gap={12} style={{ marginTop: 12 }}>
            <Button
              loading={loading}
              onClick={sign}
              disabled={!pincode}
              type="primary"
            >
              {$t("Teken af")}
            </Button>
          </Flex>
        </section>
      )}
    </section>
  );
};
